import { FC, useEffect, useRef, useState } from "react";
import { OmniGoogleMap, OmniMapMethods } from "./OmniGoogleMap";

interface LocationMapPickerProps {
    latitude?: number;
    longitude?: number;
    onLocationChange: (lat: number, lng: number) => void;
    height?: number;
}

export const LocationMapPicker: FC<LocationMapPickerProps> = ({
    latitude,
    longitude,
    onLocationChange,
    height = 400
}) => {
    const mapRef = useRef<OmniMapMethods>(null);
    const markerRef = useRef<google.maps.Marker | null>(null);

    const [center, setCenter] = useState<google.maps.LatLng>(() => {
        const hasRealCoords = latitude !== undefined && longitude !== undefined &&
                              !isNaN(latitude) && !isNaN(longitude) &&
                              !(latitude === 0 && longitude === 0);

        const lat = hasRealCoords ? latitude! : 42.3787995; // default to charlestown
        const lng = hasRealCoords ? longitude! : -71.0623995;
        return new google.maps.LatLng(lat, lng);
    });

    const hasValidCoords = latitude !== undefined && longitude !== undefined &&
                          !isNaN(latitude) && !isNaN(longitude) &&
                          !(latitude === 0 && longitude === 0);

    useEffect(() => {
        if (hasValidCoords) {
            const newCenter = new google.maps.LatLng(latitude!, longitude!);
            setCenter(newCenter);

            if (mapRef.current?.map) {
                mapRef.current.map.panTo(newCenter);
            }
        }
    }, [latitude, longitude, hasValidCoords]);

    useEffect(() => {
        const map = mapRef.current?.map;
        if (!map) return;

        // check if Google Maps API is fully loaded
        if (typeof google === 'undefined' || !google.maps || !google.maps.Marker) {
            return;
        }

        // remove existing marker if any
        if (markerRef.current) {
            try {
                markerRef.current.setMap(null);
            } catch (e) {
                console.warn('Error removing marker:', e);
            }
            markerRef.current = null;
        }

        // only create marker if we have valid coordinates
        if (!hasValidCoords && latitude === undefined && longitude === undefined) {
            return;
        }

        const markerPosition = {
            lat: center.lat(),
            lng: center.lng()
        };

        try {
            const marker = new google.maps.Marker({
                position: markerPosition,
                draggable: true,
                title: 'Location',
                animation: google.maps.Animation.DROP
            });

            // set map after creating marker to avoid timing issues
            marker.setMap(map);

            // handle marker drag
            marker.addListener('dragend', () => {
                const position = marker.getPosition();
                if (position) {
                    const lat = position.lat();
                    const lng = position.lng();
                    onLocationChange(lat, lng);
                }
            });

            markerRef.current = marker;
        } catch (e) {
            console.error('Error creating marker:', e);
        }

        return () => {
            if (markerRef.current) {
                try {
                    markerRef.current.setMap(null);
                } catch (e) {
                    console.warn('Error cleaning up marker:', e);
                }
                markerRef.current = null;
            }
        };
    }, [mapRef.current?.map, center.lat(), center.lng(), hasValidCoords, onLocationChange]);

    const handleMapClick = (latLng: google.maps.LatLng | null) => {
        if (latLng) {
            const lat = latLng.lat();
            const lng = latLng.lng();
            onLocationChange(lat, lng);
            setCenter(latLng);
        }
    };

    return (
        <div style={{ height: `${height}px`, width: '100%' }}>
            <OmniGoogleMap
                ref={mapRef}
                center={center}
                onClick={handleMapClick}
                noWrap={true}
            />
        </div>
    );
};
